import { Route } from '@angular/router';

import {
    AuthGuard,
    isGuardStatus,
    loggedOutOnlyGuard,
    PRO_ACCOUNT_STATUS,
    VerifyAccountGuard
} from '@guardfinder/shared';

export const routes: Route[] = [
    {
        path: 'welcome',
        title: 'Welcome',
        canActivate: [loggedOutOnlyGuard],
        loadComponent: () => import('../containers/welcome/welcome.component').then(m => m.WelcomeComponent)
    },
    {
        path: 'login',
        title: 'Login',
        canActivate: [loggedOutOnlyGuard],
        loadComponent: () => import('../containers/login/login.component').then(m => m.LoginComponent)
    },
    {
        path: 'reset-password',
        title: 'Reset your password',
        canActivate: [loggedOutOnlyGuard],
        loadComponent: () =>
            import('../containers/forgot-password/reset-password/reset-password.component').then(
                m => m.ResetPasswordComponent
            )
    },
    {
        path: 'verify-reset',
        title: 'Reset your password',
        canActivate: [loggedOutOnlyGuard],
        loadComponent: () =>
            import('../containers/forgot-password/password-otp/password-otp.component').then(
                m => m.PasswordOtpComponent
            )
    },
    {
        path: 'new-password',
        title: 'Create new password',
        canActivate: [loggedOutOnlyGuard],
        loadComponent: () =>
            import('../containers/forgot-password/new-password/new-password.component').then(
                m => m.NewPasswordComponent
            )
    },
    {
        path: 'register',
        title: 'Register',
        loadComponent: () =>
            import('../containers/register/register-email-password/register-email-password.component').then(
                m => m.RegisterEmailPasswordComponent
            )
    },
    {
        path: 'verify/phone',
        title: 'Verify your account',
        canActivate: [AuthGuard],
        loadComponent: () =>
            import('../containers/register/phone-verification/phone-verification.component').then(
                m => m.PhoneVerificationComponent
            )
    },
    {
        path: 'verify/account',
        title: 'Enter OTP',
        canActivate: [AuthGuard],
        loadComponent: () =>
            import('../containers/register/verify-account/verify-account.component').then(m => m.VerifyAccountComponent)
    },
    {
        path: 'onboarding',
        title: 'Welcome to GuardFinder',
        canActivate: [VerifyAccountGuard, isGuardStatus([PRO_ACCOUNT_STATUS.LEAD])],
        loadComponent: () => import('../containers/onboarding/onboarding.component').then(m => m.OnboardingComponent)
    },
    {
        path: 'application',
        title: 'Apply to be a guard',
        canActivate: [VerifyAccountGuard, isGuardStatus([PRO_ACCOUNT_STATUS.LEAD])],
        loadChildren: () => import('./application.routes').then(m => m.routes)
    },
    {
        path: 'report',
        title: 'Report an issue',
        canActivate: [AuthGuard],
        loadComponent: () => import('../containers/report/report.component').then(m => m.ReportComponent)
    },
    {
        path: 'contact-support',
        title: 'Contact support',
        canActivate: [AuthGuard],
        loadComponent: () =>
            import('../containers/settings/contact-info/contact-info.component').then(m => m.ContactInfoComponent)
    },
    {
        path: 'contact',
        title: 'Contact',
        canActivate: [AuthGuard],
        loadComponent: () => import('../containers/contact/contact.component').then(m => m.ContactComponent)
    },
    {
        path: 'terms-and-privacy',
        title: 'Terms & privacy',
        canActivate: [AuthGuard],
        loadComponent: () =>
            import('../containers/terms-privacy/terms-privacy-menu/terms-privacy.component').then(
                m => m.TermsPrivacyComponent
            )
    },
    {
        path: 'job/:id',
        canActivate: [AuthGuard, isGuardStatus([PRO_ACCOUNT_STATUS.ACTIVE, PRO_ACCOUNT_STATUS.VACATION])],
        loadComponent: () =>
            import('../containers/job/job-details/job-details.component').then(m => m.JobDetailsComponent)
    },
    {
        path: 'job/:id/rate-and-review',
        canActivate: [AuthGuard, isGuardStatus([PRO_ACCOUNT_STATUS.ACTIVE, PRO_ACCOUNT_STATUS.VACATION])],
        loadComponent: () =>
            import('../containers/job/job-rate-review/job-rate-review.component').then(m => m.JobRateReviewComponent)
    },
    {
        path: 'shift/:id',
        canActivate: [AuthGuard, isGuardStatus([PRO_ACCOUNT_STATUS.ACTIVE, PRO_ACCOUNT_STATUS.VACATION])],
        title: route => `Shift #${route.params['id']}`,
        loadComponent: () =>
            import('../containers/job/shift-details/shift-details.component').then(m => m.ShiftDetailsComponent)
    },
    {
        path: 'notifications',
        title: 'Your notifications',
        canActivate: [AuthGuard, isGuardStatus([PRO_ACCOUNT_STATUS.ACTIVE, PRO_ACCOUNT_STATUS.VACATION])],
        loadComponent: () =>
            import('../containers/notifications/notifications.component').then(m => m.NotificationsComponent)
    },
    {
        path: 'job',
        redirectTo: '/',
        pathMatch: 'full'
    },
    {
        path: 'client/:id',
        canActivate: [AuthGuard, isGuardStatus([PRO_ACCOUNT_STATUS.ACTIVE, PRO_ACCOUNT_STATUS.VACATION])],
        loadComponent: () => import('../containers/client/client.component').then(m => m.ClientComponent)
    },
    {
        path: 'client',
        pathMatch: 'full',
        redirectTo: '/'
    },
    {
        path: 'available-jobs',
        title: 'Jobs',
        canActivate: [AuthGuard, isGuardStatus([PRO_ACCOUNT_STATUS.ACTIVE, PRO_ACCOUNT_STATUS.VACATION])],
        loadComponent: () =>
            import('../containers/jobs-available-list/jobs-available-list.component').then(
                m => m.JobsAvailableListComponent
            )
    },
    {
        path: 'setting',
        canActivate: [
            AuthGuard,
            isGuardStatus([
                PRO_ACCOUNT_STATUS.LEAD,
                PRO_ACCOUNT_STATUS.APPLICANT,
                PRO_ACCOUNT_STATUS.ACTIVE,
                PRO_ACCOUNT_STATUS.VACATION
            ])
        ],
        loadChildren: () => import('./settings.routes').then(m => m.routes)
    },
    {
        path: 'settings',
        title: 'Settings',
        canActivate: [
            AuthGuard,
            isGuardStatus([
                PRO_ACCOUNT_STATUS.LEAD,
                PRO_ACCOUNT_STATUS.APPLICANT,
                PRO_ACCOUNT_STATUS.ACTIVE,
                PRO_ACCOUNT_STATUS.VACATION
            ])
        ],
        loadComponent: () =>
            import('../containers/settings/settings-main/settings-main.component').then(m => m.SettingsMainComponent)
    },
    {
        path: 'preferences/profile',
        title: 'Make your account more attractive to clients',
        canActivate: [AuthGuard, isGuardStatus([PRO_ACCOUNT_STATUS.ACTIVE, PRO_ACCOUNT_STATUS.VACATION])],
        loadComponent: () =>
            import('../containers/preferences-quiz/preferences-profile/preferences-profile.component').then(
                m => m.PreferencesProfileComponent
            )
    },
    {
        path: 'preferences/availability',
        title: 'Set your availability',
        canActivate: [AuthGuard, isGuardStatus([PRO_ACCOUNT_STATUS.ACTIVE, PRO_ACCOUNT_STATUS.VACATION])],
        loadComponent: () =>
            import('../containers/preferences-quiz/preferences-availability/preferences-availability.component').then(
                m => m.PreferencesAvailabilityComponent
            )
    },
    {
        path: 'preferences/completed',
        title: 'Completed',
        canActivate: [AuthGuard, isGuardStatus([PRO_ACCOUNT_STATUS.ACTIVE, PRO_ACCOUNT_STATUS.VACATION])],
        loadComponent: () =>
            import('../containers/preferences-quiz/preferences-completed/preferences-completed.component').then(
                m => m.PreferencesCompletedComponent
            )
    },
    {
        path: '',
        canActivate: [AuthGuard],
        loadChildren: () => import('./tabs.routes').then(m => m.routes)
    },
    {
        path: '',
        pathMatch: 'full',
        redirectTo: ''
    },
    { path: '**', redirectTo: '' }
];
